import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Save, Loader2, Bug, Search, CheckCircle2, AlertTriangle, Users, Pencil, RefreshCw } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import FormError from "./ui/FormError";
import useEscClose from "../hooks/useEscClose";
import BulkBatchesModal from "./BulkBatchesModal";
import { confirmDialog } from "../utils/dialogs";

const MEDS = [
  { value: "albendazole",  label: "Albendazole",  default_dose: "400 mg single dose" },
  { value: "mebendazole",  label: "Mebendazole",  default_dose: "100 mg twice daily for 3 days" },
  { value: "ivermectin",   label: "Ivermectin",   default_dose: "200 µg/kg single dose" },
  { value: "pyrantel",     label: "Pyrantel pamoate", default_dose: "11 mg/kg single dose" },
  { value: "praziquantel", label: "Praziquantel", default_dose: "" },
  { value: "other",        label: "Other (specify)…", default_dose: "" },
];

// WHO recommends a 6-month interval between deworming doses. Anything under
// 5 months (150 days) after the last recorded dose triggers an inadequate-
// gap warning so a coach doesn't accidentally double-dose.
const MIN_GAP_DAYS = 150;

/** Days between two ISO YYYY-MM-DD dates. Returns null if either is missing
 *  or unparseable. Negative when `newDate` is BEFORE `lastDate` (backdated).
 */
function gapDays(newDate, lastDate) {
  if (!newDate || !lastDate) return null;
  const a = new Date(`${newDate}T00:00:00Z`);
  const b = new Date(`${lastDate}T00:00:00Z`);
  if (isNaN(a) || isNaN(b)) return null;
  return Math.round((a - b) / 86400000);
}

/**
 * "Squad deworming day" — bulk wizard for enmasse dosing. Lists every
 * athlete with their current deworming urgency chip, lets you filter to
 * just the overdue/due cohort, tick whoever you actually dosed today, set
 * a single date + medication + dose, and submit a single bulk request.
 *
 * Guardrails (2026-02-11):
 *   • Every displayed date is rendered as DD/MM/YYYY (Indian convention).
 *   • A per-athlete "recent dose" warning flags anyone dewormed < 5 months
 *     (150d) ago so a coach doesn't double-dose. A summary chip on the
 *     header + a blocking confirm on submit makes it hard to miss.
 *   • After the bulk apply, each freshly-created record is editable in
 *     place — mis-dated entries can be corrected without a full round-
 *     trip through the per-athlete Data Editor.
 */
export default function BulkDewormingModal({ open, onClose, onSaved }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [medication, setMedication] = useState("albendazole");
  const [medicationOther, setMedicationOther] = useState("");
  const [dose, setDose] = useState("400 mg single dose");
  const [prescribedBy, setPrescribedBy] = useState("");
  const [notes, setNotes] = useState("");

  const [schedule, setSchedule] = useState(null);
  const [groupFilter, setGroupFilter] = useState("all");
  const [urgencyFilter, setUrgencyFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  // Existing-batches lookup: if the user picks a date that already has a
  // bulk deworming batch, offer "edit existing" instead of forcing a new
  // batch. Loaded once when the modal opens.
  const [allBatches, setAllBatches] = useState([]);
  const [showBatchesModal, setShowBatchesModal] = useState(null); // batch_id to focus, or null

  useEscClose(open, () => { if (!busy) onClose?.(); });

  useEffect(() => {
    if (!open) return;
    setError("");
    setResult(null);
    setPicked(new Set());
    setSearch("");
    setGroupFilter("all");
    setUrgencyFilter("all");
    setDate(new Date().toISOString().slice(0, 10));
    setMedication("albendazole");
    setDose("400 mg single dose");
    api.get("/squad/schedule")
      .then((r) => setSchedule(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load athlete list"));
    // Also fetch existing deworming batches so we can show the "edit
    // existing" chip when the coach picks a date that already has one.
    api.get("/bulk-batches", { params: { kind: "deworming" } })
      .then((r) => setAllBatches(r.data?.items || []))
      .catch(() => setAllBatches([]));
  }, [open]);

  // Find any existing batch for the currently-picked date. Only surfaced
  // when the coach hasn't yet selected any athletes (otherwise we're mid-
  // creation and the offer would be noise).
  const existingBatchOnDate = useMemo(() => {
    if (!date || allBatches.length === 0) return null;
    return allBatches.find((b) => b.date === date) || null;
  }, [date, allBatches]);

  const onMedChange = (val) => {
    const m = MEDS.find((x) => x.value === val);
    setMedication(val);
    setDose(m?.default_dose || dose);
  };

  const groups = useMemo(() => {
    if (!schedule?.items) return [];
    return [...new Set(schedule.items.map((s) => s.sailor_group || "—"))].sort();
  }, [schedule]);

  const items = useMemo(() => {
    if (!schedule?.items) return [];
    const q = search.trim().toLowerCase();
    return schedule.items.filter((it) => {
      if (groupFilter !== "all" && (it.sailor_group || "—") !== groupFilter) return false;
      if (urgencyFilter === "overdue" && it.deworming_urgency !== "red") return false;
      if (urgencyFilter === "due"     && !["yellow","orange"].includes(it.deworming_urgency)) return false;
      if (urgencyFilter === "missing" && it.deworming_urgency !== "missing") return false;
      if (q && !it.sailor_name.toLowerCase().includes(q)) return false;
      return true;
    }).sort((a, b) => {
      // Overdue + missing float to the top.
      const rank = (u) => ({ red: 0, missing: 1, orange: 2, yellow: 3, ok: 4 }[u] ?? 5);
      return rank(a.deworming_urgency) - rank(b.deworming_urgency) || a.sailor_name.localeCompare(b.sailor_name);
    });
  }, [schedule, search, groupFilter, urgencyFilter]);

  // Count selected athletes whose gap-since-last-dose is inadequate (< 150d).
  // Drives both the header warning and the confirm dialog on submit.
  const inadequateGapSelected = useMemo(() => {
    if (!schedule?.items) return [];
    return schedule.items.filter((it) => {
      if (!picked.has(it.sailor_id)) return false;
      const g = gapDays(date, it.last_deworming_date);
      return g != null && g >= 0 && g < MIN_GAP_DAYS;
    });
  }, [schedule, picked, date]);

  const toggleOne = (sid) => {
    setPicked((p) => {
      const n = new Set(p);
      n.has(sid) ? n.delete(sid) : n.add(sid);
      return n;
    });
  };

  const selectAllVisible = () => setPicked((p) => new Set([...p, ...items.map((it) => it.sailor_id)]));
  const clearAll = () => setPicked(new Set());

  const submit = async () => {
    if (picked.size === 0) { setError("Pick at least one athlete"); return; }
    if (medication === "other" && !medicationOther.trim()) { setError("Specify the medication name"); return; }
    if (inadequateGapSelected.length > 0) {
      const names = inadequateGapSelected.slice(0, 5).map((s) => s.sailor_name).join(", ");
      const more = inadequateGapSelected.length > 5 ? ` and ${inadequateGapSelected.length - 5} more` : "";
      const ok = await confirmDialog(
        `${inadequateGapSelected.length} athlete${inadequateGapSelected.length === 1 ? "" : "s"} ` +
        `${inadequateGapSelected.length === 1 ? "was" : "were"} dewormed less than 5 months ago ` +
        `(${names}${more}).\n\nWHO recommends a 6-month gap between doses. Proceed anyway?`,
        { confirmLabel: "Proceed", danger: true },
      );
      if (!ok) return;
    }
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/dewormings/bulk", {
        date,
        medication,
        medication_other: medication === "other" ? medicationOther.trim() : null,
        dose: dose.trim() || null,
        prescribed_by: prescribedBy.trim() || null,
        notes: notes.trim() || null,
        sailor_ids: [...picked],
      });
      // Seed each created row with an editable-date state so the coach can
      // fix mistyped dates immediately in the result panel.
      setResult({
        ...r.data,
        created: (r.data.created || []).map((c) => ({ ...c, date })),
      });
      await onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Bulk save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 px-4"
      data-testid="bulk-deworm-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <div className="card w-full max-w-4xl p-5 md:p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
              <Bug size={11} /> Squad deworming day
            </div>
            <div className="font-display text-lg text-slate-100 mt-0.5">Bulk dose multiple athletes</div>
            <div className="text-[11px] text-slate-400 mt-0.5">Set the date + medication once, then tick everyone you actually dosed.</div>
          </div>
          <button onClick={onClose} className="pill !p-1.5" data-testid="bulk-deworm-close"><X size={14} /></button>
        </div>

        {result ? (
          <BulkResult result={result} setResult={setResult} onAnother={() => { setResult(null); setPicked(new Set()); }} onClose={onClose} onSaved={onSaved} />
        ) : (
          <>
            {error && <div className="mb-3"><FormError message={error} onDismiss={() => setError("")} testId="bulk-deworm-error" /></div>}

            {/* Dose block */}
            <div className="card !bg-[#0B1627] p-3 mb-3 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="bulk-deworm-dose-block">
              <label className="block">
                <div className="text-[9px] uppercase tracking-widest text-slate-400">Date given *</div>
                <input type="date" required value={date} onChange={(e) => setDate(e.target.value)}
                       className="form-input w-full font-mono-data" data-testid="bulk-deworm-date" />
                <div className="text-[9px] text-slate-500 mt-0.5 font-mono-data">
                  DD/MM/YYYY · <span className="text-cyan-300" data-testid="bulk-deworm-date-preview">{fmtDate(date)}</span>
                </div>
              </label>
              <label className="block">
                <div className="text-[9px] uppercase tracking-widest text-slate-400">Medication *</div>
                <select value={medication} onChange={(e) => onMedChange(e.target.value)}
                        className="form-input w-full" data-testid="bulk-deworm-med">
                  {MEDS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
              {medication === "other" && (
                <label className="block md:col-span-2">
                  <div className="text-[9px] uppercase tracking-widest text-slate-400">Medication name</div>
                  <input type="text" maxLength={80} value={medicationOther}
                         onChange={(e) => setMedicationOther(e.target.value)}
                         className="form-input w-full" data-testid="bulk-deworm-med-other" />
                </label>
              )}
              <label className="block">
                <div className="text-[9px] uppercase tracking-widest text-slate-400">Dose</div>
                <input type="text" maxLength={80} value={dose} onChange={(e) => setDose(e.target.value)}
                       className="form-input w-full" data-testid="bulk-deworm-dose" />
              </label>
              <label className="block">
                <div className="text-[9px] uppercase tracking-widest text-slate-400">Prescribed by</div>
                <input type="text" maxLength={120} value={prescribedBy}
                       onChange={(e) => setPrescribedBy(e.target.value)}
                       placeholder="Dr. ___"
                       className="form-input w-full" data-testid="bulk-deworm-prescriber" />
              </label>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input type="text" placeholder="Search athlete…" value={search}
                       onChange={(e) => setSearch(e.target.value)}
                       className="pl-7 pr-3 py-1.5 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60"
                       data-testid="bulk-deworm-search" />
              </div>
              <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}
                      className="py-1.5 px-2 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-[11px] text-slate-200"
                      data-testid="bulk-deworm-group">
                <option value="all">All groups</option>
                {groups.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <select value={urgencyFilter} onChange={(e) => setUrgencyFilter(e.target.value)}
                      className="py-1.5 px-2 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-[11px] text-slate-200"
                      data-testid="bulk-deworm-urgency">
                <option value="all">All urgencies</option>
                <option value="overdue">Only overdue</option>
                <option value="due">Only due ≤5d</option>
                <option value="missing">Never dewormed</option>
              </select>
              <div className="ml-auto flex items-center gap-2 text-[11px]">
                <span className="text-slate-400 font-mono-data" data-testid="bulk-deworm-counter">
                  <Users size={11} className="inline mr-1" />{picked.size} selected · {items.length} shown
                </span>
                <button onClick={selectAllVisible} className="pill !text-[11px] !py-1 !px-2.5" data-testid="bulk-deworm-select-all">Select all shown</button>
                <button onClick={clearAll} className="pill !text-[11px] !py-1 !px-2.5" data-testid="bulk-deworm-clear">Clear</button>
              </div>
            </div>

            {existingBatchOnDate && (
              <div className="mb-2 rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-[11px] text-cyan-100 flex items-start gap-2"
                   data-testid="bulk-deworm-existing-batch">
                <RefreshCw size={12} className="mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold">
                    A bulk deworming batch already exists for {fmtDate(date)}
                  </div>
                  <div className="text-cyan-200/80 text-[10px] mt-0.5">
                    {existingBatchOnDate.athlete_count} athlete{existingBatchOnDate.athlete_count === 1 ? "" : "s"}
                    {" · "}
                    {existingBatchOnDate.data?.medication === "other"
                      ? (existingBatchOnDate.data?.medication_other || "Other")
                      : (existingBatchOnDate.data?.medication || "—")}
                    . Do you want to edit older data or add fresh?
                  </div>
                </div>
                <button onClick={() => setShowBatchesModal(existingBatchOnDate.batch_id)}
                        className="pill !text-[10px] !py-1 !px-2 active"
                        data-testid="bulk-deworm-edit-existing">
                  <Pencil size={10} /> Edit older data
                </button>
              </div>
            )}

            {inadequateGapSelected.length > 0 && (
              <div className="mb-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 flex items-start gap-2"
                   data-testid="bulk-deworm-gap-warning">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">
                    {inadequateGapSelected.length} selected athlete{inadequateGapSelected.length === 1 ? "" : "s"} dewormed &lt; 5 months ago
                  </div>
                  <div className="text-amber-300/80 text-[10px] mt-0.5">
                    WHO recommends a 6-month gap. You&apos;ll be asked to confirm before applying.
                  </div>
                </div>
              </div>
            )}

            {/* List */}
            <div className="card !bg-[#0B1627] p-0 max-h-[45vh] overflow-y-auto" data-testid="bulk-deworm-list">
              {!schedule ? (
                <div className="p-6 text-center text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" /> Loading athletes…</div>
              ) : items.length === 0 ? (
                <div className="p-6 text-center text-slate-500 text-[12px]">No athletes match this filter.</div>
              ) : (
                <div className="divide-y divide-[#1E304B]">
                  {items.map((it) => (
                    <AthleteRow key={it.sailor_id} it={it} checked={picked.has(it.sailor_id)}
                                gapWarning={(() => {
                                  const g = gapDays(date, it.last_deworming_date);
                                  return g != null && g >= 0 && g < MIN_GAP_DAYS ? g : null;
                                })()}
                                onToggle={() => toggleOne(it.sailor_id)} />
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-3">
              <button className="pill" onClick={onClose} disabled={busy} data-testid="bulk-deworm-cancel">Cancel</button>
              <button onClick={submit} disabled={busy || picked.size === 0}
                      className="pill active disabled:opacity-40" data-testid="bulk-deworm-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Apply to {picked.size} athlete{picked.size === 1 ? "" : "s"}
              </button>
            </div>
          </>
        )}
      </div>
      <BulkBatchesModal
        open={showBatchesModal != null}
        onClose={() => setShowBatchesModal(null)}
        focusBatchId={showBatchesModal}
      />
    </div>,
    document.body,
  );
}

function urgencyChip(urgency) {
  const m = {
    red:     { bg: "bg-rose-500/15",    text: "text-rose-200",    border: "border-rose-500/40",    label: "Overdue" },
    orange:  { bg: "bg-orange-500/15",  text: "text-orange-200",  border: "border-orange-500/40",  label: "Due soon" },
    yellow:  { bg: "bg-amber-500/15",   text: "text-amber-200",   border: "border-amber-500/40",   label: "Upcoming" },
    ok:      { bg: "bg-emerald-500/10", text: "text-emerald-200", border: "border-emerald-500/30", label: "On track" },
    missing: { bg: "bg-slate-500/10",   text: "text-slate-300",   border: "border-slate-500/30",   label: "Never" },
  }[urgency] || { bg: "", text: "text-slate-400", border: "border-slate-500/20", label: "—" };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono-data border ${m.border} ${m.bg} ${m.text}`}>
      {m.label}
    </span>
  );
}

function AthleteRow({ it, checked, gapWarning, onToggle }) {
  return (
    <label className={`px-3 py-2 flex items-center gap-3 cursor-pointer hover:bg-[#0E1A2E] ${checked ? "bg-cyan-500/[0.06]" : ""}`}
           data-testid={`bulk-deworm-row-${it.sailor_id}`}>
      <input type="checkbox" checked={checked} onChange={onToggle}
             className="accent-cyan-400 w-4 h-4" data-testid={`bulk-deworm-check-${it.sailor_id}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-100">{it.sailor_name}</span>
          <span className="text-[10px] text-slate-500 font-mono-data">{it.sailor_group}</span>
          {urgencyChip(it.deworming_urgency)}
          {gapWarning != null && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono-data border border-amber-500/50 bg-amber-500/15 text-amber-200"
                  title={`Last dose ${gapWarning}d ago — less than the WHO-recommended 6-month gap`}
                  data-testid={`bulk-deworm-gap-${it.sailor_id}`}>
              <AlertTriangle size={9} /> Gap {gapWarning}d
            </span>
          )}
        </div>
        <div className="text-[10px] text-slate-500 font-mono-data mt-0.5">
          {it.last_deworming_date ? `last on ${fmtDate(it.last_deworming_date)}` : "no prior record"}
          {it.next_deworming_due && ` · next due ${fmtDate(it.next_deworming_due)}`}
          {it.deworming_days_until != null && (
            <span className={it.deworming_days_until < 0 ? "text-rose-300" : ""}>
              {" "}({it.deworming_days_until < 0 ? `${Math.abs(it.deworming_days_until)}d overdue` : `in ${it.deworming_days_until}d`})
            </span>
          )}
        </div>
      </div>
    </label>
  );
}

function BulkResult({ result, setResult, onAnother, onClose, onSaved }) {
  const c = result.counts || {};
  const patchCreatedDate = (recordId, newDate) => {
    setResult((r) => ({
      ...r,
      created: r.created.map((row) =>
        row.record_id === recordId ? { ...row, date: newDate } : row,
      ),
    }));
  };
  return (
    <div data-testid="bulk-deworm-result">
      <div className="card !bg-emerald-500/10 !border-emerald-500/30 p-4 mb-3 flex items-center gap-3">
        <CheckCircle2 size={20} className="text-emerald-300 shrink-0" />
        <div>
          <div className="font-display text-base text-emerald-200">
            Dewormed {c.created} athlete{c.created === 1 ? "" : "s"}
          </div>
          <div className="text-[11px] text-slate-400">Next deworming due in 6 months — schedule auto-updated. Fix any mis-dated rows below.</div>
        </div>
      </div>
      {c.skipped > 0 && (
        <div className="card !bg-amber-500/10 !border-amber-500/30 p-3 mb-3">
          <div className="text-[11px] uppercase tracking-widest text-amber-300 flex items-center gap-1.5 mb-1">
            <AlertTriangle size={11} /> Skipped {c.skipped} (already dewormed on this date)
          </div>
          <div className="text-[11px] text-slate-300 font-mono-data">
            {result.skipped.map((s) => s.sailor_name).join(", ")}
          </div>
        </div>
      )}
      {result.created?.length > 0 && (
        <div className="text-[11px] text-slate-400 font-mono-data">
          <div className="uppercase tracking-widest mb-1 text-slate-500">Applied to · click a date to correct</div>
          <div className="max-h-64 overflow-y-auto bg-[#0B1627] rounded border border-[#1E304B] divide-y divide-[#1E304B]">
            {result.created.map((row) => (
              <CreatedRow key={row.record_id} row={row}
                          onDateSaved={(nd) => patchCreatedDate(row.record_id, nd)}
                          onSaved={onSaved} />
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-4">
        <button onClick={onAnother} className="pill" data-testid="bulk-deworm-another">Apply again to others</button>
        <button onClick={onClose} className="pill active" data-testid="bulk-deworm-done">Done</button>
      </div>
    </div>
  );
}

function CreatedRow({ row, onDateSaved, onSaved }) {
  // Inline date editor for a freshly-created record. Kept collapsed until
  // the coach clicks the pencil so the result panel stays scannable.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.date);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    if (!draft || draft === row.date) { setEditing(false); return; }
    setBusy(true);
    setErr("");
    try {
      await api.put(`/sailors/${row.sailor_id}/dewormings/${row.record_id}`, { date: draft });
      onDateSaved?.(draft);
      await onSaved?.();
      setEditing(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Could not update date");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-3 py-2 flex items-center gap-2" data-testid={`bulk-deworm-created-${row.record_id}`}>
      <span className="text-slate-200 flex-1 truncate">· {row.sailor_name}</span>
      {editing ? (
        <>
          <input type="date" value={draft} onChange={(e) => setDraft(e.target.value)}
                 className="form-input !py-1 !text-[11px] font-mono-data"
                 data-testid={`bulk-deworm-created-date-input-${row.record_id}`} />
          <button onClick={save} disabled={busy}
                  className="pill !text-[10px] !py-1 !px-2 active"
                  data-testid={`bulk-deworm-created-save-${row.record_id}`}>
            {busy ? <Loader2 size={10} className="animate-spin" /> : "Save"}
          </button>
          <button onClick={() => { setDraft(row.date); setEditing(false); setErr(""); }}
                  className="pill !text-[10px] !py-1 !px-2"
                  data-testid={`bulk-deworm-created-cancel-${row.record_id}`}>
            Cancel
          </button>
        </>
      ) : (
        <>
          <span className="text-slate-300 font-mono-data" data-testid={`bulk-deworm-created-date-${row.record_id}`}>
            {fmtDate(row.date)}
          </span>
          <button onClick={() => setEditing(true)}
                  className="pill !text-[10px] !py-1 !px-2"
                  data-testid={`bulk-deworm-created-edit-${row.record_id}`}
                  title="Correct the date">
            <Pencil size={10} />
          </button>
        </>
      )}
      {err && <span className="text-rose-300 text-[10px] ml-2">{err}</span>}
    </div>
  );
}
